'use client'

import { LoaderCircle, Search } from 'lucide-react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { FormilySchemaRenderer } from '@/components/formily/renderer'
import { SuperPromptItem } from '@/components/super-prompts/super-prompt-item'
import { useSuperPromptsGrouping } from '@/components/super-prompts/use-super-prompts-grouping'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useDynamicI18n } from '@/hooks/use-dynamic-i18n'
import {
  useFetchSuperPrompts,
  useSuperPromptsStore,
} from '@/hooks/use-super-prompts-store'
import type { ISuperPromptModel } from '@/lib/db/types'
import { cn } from '@/lib/utils'

interface SuperPromptsModalProps {
  onClose: () => void
  onSuperPromptSelect: (prompt: ISuperPromptModel, promptText: string) => void
}

export function SuperPromptsModal({
  onClose,
  onSuperPromptSelect,
}: SuperPromptsModalProps) {
  const t = useTranslations('ai-chat')
  const { dt } = useDynamicI18n()

  const { loading, superPrompts } = useSuperPromptsStore()

  // Fetch super prompts on mount
  useFetchSuperPrompts()

  const [isAddingPrompt, setIsAddingPrompt] = useState<string | null>(null)
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [search, setSearch] = useState('')

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => {
      window.removeEventListener('resize', checkMobile)
    }
  }, [])

  // Close expanded prompt when clicking elsewhere (mobile)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && expandedPrompt) {
        const target = event.target as Element
        if (!target.closest('[data-expanded-prompt]')) {
          setExpandedPrompt(null)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMobile, expandedPrompt])

  // Handle mobile tap interaction
  const handleMobileTap = (prompt: ISuperPromptModel) => {
    const promptId = prompt._id.toString()

    if (expandedPrompt === promptId) {
      // Second tap - select the prompt
      handleSuperPromptSelect(prompt)
    } else {
      // First tap - expand the details
      setExpandedPrompt(promptId)
    }
  }

  // Handle super prompt selection
  const handleSuperPromptSelect = async (prompt: ISuperPromptModel) => {
    setIsAddingPrompt(prompt._id.toString())

    try {
      // Set the prompt text based on category
      const promptMessage = dt(
        prompt.add_default_placeholder ||
          (prompt.category === 'Embed'
            ? `Replace this message with the ${
                prompt.search_name || ''
              } link you want to embed and some instructions for how you want to use it`
            : `Replace this message with some instructions for how you want to use ${prompt.name}`),
      )

      // Call the parent handler with the prompt and text
      onSuperPromptSelect(prompt, promptMessage)

      // Close the modal
      onClose()

      // Show success toast
      toast.success(t('added_super_prompt_to_chat', { name: prompt.name }))
    } catch (_error) {
      toast.error(t('failed_to_add_super_prompt_to_chat'))
    } finally {
      setIsAddingPrompt(null)
    }
  }

  // Group and filter prompts using shared hook
  const groupedData = useSuperPromptsGrouping(superPrompts, search)

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[60vh] overflow-y-auto">
        {/* Header */}
        <DialogHeader>
          <DialogTitle>{t('super_prompts')}</DialogTitle>
        </DialogHeader>

        <Input
          pre={<Search className="size-4" />}
          type="search"
          placeholder={t('search_super_prompts')}
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Content */}
        <div className="flex-1 relative" style={{ overflow: 'hidden' }}>
          {loading && (
            <div className="absolute z-10 inset-0 bg-black/20 flex items-center justify-center">
              <LoaderCircle className="w-8 h-8 text-white animate-spin" />
            </div>
          )}

          <div
            className="h-full flex flex-col overflow-y-auto no-scrollbar p-4"
            style={{ overflowX: 'visible' }}
          >
            {groupedData.isEmpty ? (
              <div className="flex items-center justify-center h-32 text-white/60">
                <p className="text-sm">
                  {search.trim()
                    ? t('no_super_prompts_found_matching', { search })
                    : t('no_super_prompts')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {groupedData.categories.map((category, index) => (
                  <div key={category}>
                    {/* Category title */}
                    <div className={cn('pb-2', index === 0 && 'pt-0')}>
                      <h3 className="text-white text-xs font-normal py-1">
                        {category}
                      </h3>
                    </div>

                    {/* Prompts in this category */}
                    <div className="space-y-2">
                      {groupedData.groupedPrompts[category].map((prompt) => {
                        const promptId = prompt._id.toString()
                        const isExpanded = expandedPrompt === promptId
                        const isAdding = isAddingPrompt === promptId

                        return (
                          <div key={promptId} className="space-y-2">
                            {/* Desktop: HoverCard, Mobile: Clickable item */}
                            {isMobile ? (
                              <div
                                className={cn(
                                  'flex items-center gap-3 px-3 py-3 hover:bg-white/10 cursor-pointer transition-colors group rounded-lg',
                                  isAdding && 'opacity-50 cursor-not-allowed',
                                  isExpanded && 'bg-white/10',
                                )}
                                onClick={() => {
                                  if (isAdding) return
                                  handleMobileTap(prompt)
                                }}
                              >
                                {/* Icon */}
                                <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
                                  {prompt.logo ? (
                                    <img
                                      src={prompt.logo}
                                      alt={prompt.name}
                                      width={24}
                                      height={24}
                                      className="object-contain"
                                    />
                                  ) : (
                                    <div className="w-6 h-6 rounded-sm" />
                                  )}
                                </div>

                                {/* Name */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-white text-sm font-medium truncate transition-colors">
                                    {prompt.name}
                                  </p>
                                </div>

                                {/* Loading indicator or expand indicator */}
                                {isAdding ? (
                                  <div className="flex-shrink-0">
                                    <LoaderCircle className="w-4 h-4 animate-spin text-white/60" />
                                  </div>
                                ) : (
                                  <div className="flex-shrink-0 text-white/40 text-xs">
                                    {isExpanded ? 'Tap to Add' : 'Tap for Info'}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <SuperPromptItem
                                prompt={prompt}
                                isLoading={isAdding}
                                onClick={handleSuperPromptSelect}
                                hoverCardSideOffset={-32}
                              />
                            )}

                            {/* Mobile expanded details */}
                            {isMobile && isExpanded && (
                              <div
                                data-expanded-prompt
                                className="bg-[rgba(55, 55, 55, 0.8)] backdrop-blur-sm rounded-lg p-4 border border-white/10 animate-in slide-in-from-top duration-200"
                              >
                                <div className="space-y-3">
                                  {/* Header with icon and name */}
                                  <div className="flex items-center gap-3">
                                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                                      {prompt.logo ? (
                                        <img
                                          src={prompt.logo}
                                          alt={prompt.name}
                                          width={32}
                                          height={32}
                                          className="object-contain"
                                        />
                                      ) : (
                                        <div className="w-8 h-8 rounded-sm bg-white/10" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <h3 className="text-white text-base font-semibold">
                                        {prompt.name}
                                      </h3>
                                    </div>
                                  </div>

                                  {/* Description */}
                                  <p className="text-gray-300 text-sm leading-relaxed">
                                    {dt(prompt.description) || 'No description'}
                                  </p>

                                  {/* Example image */}
                                  {prompt.example && (
                                    <div className="relative w-full h-32 bg-[rgba(255,255,255,0.1)] rounded-lg overflow-hidden">
                                      <Image
                                        src={prompt.example}
                                        alt={`${prompt.name} example`}
                                        fill
                                        className="object-cover transition-opacity duration-300"
                                        sizes="100vw"
                                        placeholder="blur"
                                        blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                                        loading="lazy"
                                        onLoad={(e) => {
                                          e.currentTarget.style.opacity = '1'
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none'
                                        }}
                                        style={{ opacity: '0' }}
                                      />
                                      <div className="absolute inset-0 bg-gradient-to-br from-gray-300/10 to-gray-400/10 animate-pulse" />
                                    </div>
                                  )}

                                  {/* Formily content if available */}
                                  {prompt.parameters?.prompt &&
                                    Object.values(
                                      prompt.parameters?.prompt?.properties ||
                                        {},
                                    ).some(
                                      (property: any) =>
                                        property.type === 'void',
                                    ) && (
                                      <div className="border-t border-white/10 pt-3">
                                        <FormilySchemaRenderer
                                          formilySchema={
                                            prompt.parameters?.prompt
                                          }
                                          previewMode={true}
                                          needTranslation={true}
                                          onlyRenderUiNodes={true}
                                        />
                                      </div>
                                    )}

                                  {/* Action buttons */}
                                  <div className="flex items-center gap-3 pt-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setExpandedPrompt(null)}
                                      className="text-white/60 hover:text-white hover:bg-white/10"
                                    >
                                      Close
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        handleSuperPromptSelect(prompt)
                                      }
                                      disabled={isAdding}
                                      className="bg-white text-black hover:bg-white/90 font-medium"
                                    >
                                      {isAdding ? (
                                        <>
                                          <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
                                          Adding...
                                        </>
                                      ) : (
                                        'Add to Project'
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
